import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Query, QueryFilters } from '@tanstack/react-query';

import { WATCHLIST_QUERY_KEY } from '@/hooks/useWatchlist';
import type { WatchlistQueryClientData } from '@/hooks/useWatchlist';

const queryFiltersFor = (watchlistId: string): QueryFilters => ({
  queryKey: WATCHLIST_QUERY_KEY,
  predicate(query: unknown) {
    // assume a query from the (watchlist) WATCHLIST_QUERY_KEY is type WatchlistQueryClientData
    const queryId = (query as Query<WatchlistQueryClientData>).state?.data?.id;
    return queryId === watchlistId;
  },
});

/**
 * useWatchlistQueryClient is a wrapper around the react-query 'queryClient',
 * and should be used instead of useQueryClient for queryClient functionality
 * that acts on a 'Watchlist' object.
 *
 * A watchlist object may be present in the react-query client under multiple
 * query keys, specifically: [QUERY_KEY, {id}, ...] & [QUERY_KEY, null, ...].
 * useWatchlistQueryClient ensures that all objects are updated refetched etc
 * for a given watchlist id.
 */
const useWatchlistQueryClient = () => {
  const queryClient = useQueryClient();

  const watchlistQueryClient = useMemo(() => {
    const refetchQueries = (watchlistId: string) =>
      queryClient.refetchQueries(queryFiltersFor(watchlistId));

    const invalidateQueries = (watchlistId: string) =>
      queryClient.invalidateQueries(queryFiltersFor(watchlistId));

    const setQueriesData = (
      watchlistId: string,
      updater: (
        input: NonNullable<WatchlistQueryClientData>
      ) => NonNullable<WatchlistQueryClientData>
    ) =>
      queryClient.setQueriesData<WatchlistQueryClientData>(
        queryFiltersFor(watchlistId),
        (state) => {
          if (typeof state === 'undefined') {
            return state;
          }
          return updater(state);
        }
      );

    return {
      setQueriesData,
      refetchQueries,
      invalidateQueries,
    };
  }, [queryClient]);

  return watchlistQueryClient;
};

export default useWatchlistQueryClient;
